import { useState, useEffect, useRef } from 'react'; // useRef は useInView hook 内で使用
import { Link } from 'react-router-dom';

// ── スクロールアニメーション hook ───────────────────────────────────
const useInView = (threshold = 0.15) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
};

// ── SVG アイコン ────────────────────────────────────────────────────
const MapPinIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
);
const MsgIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const CheckCircleIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);
const PhoneOffIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92V19a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 1h2.09a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11z" />
        <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
);
const CreditCardIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
);
const FileCheckIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><polyline points="9 15 11 17 15 13" />
    </svg>
);
const CheckSquareIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
);
const SendIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#005c53" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
);
const ArrowRightIcon = ({ color = '#005c53', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);
const ChevronDownIcon = ({ open }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const VolumeXIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
    </svg>
);
const VolumeIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
);

// ── データ ──────────────────────────────────────────────────────────
const features = [
    { Icon: MapPinIcon,      title: '地元の専門施工業者に相談', desc: 'エリアや実績で絞り込み、地元の施工業者をすぐに見つけられます', iconBg: 'linear-gradient(135deg,#e6f2f1,#d1f5f0)' },
    { Icon: PhoneOffIcon,    title: '業者を選ぶまで営業連絡なし', desc: 'お客様が指定した業者のみから連絡がきます。勝手な営業はありません', iconBg: 'linear-gradient(135deg,#e0e7ff,#c7d2fe)' },
    { Icon: MsgIcon,         title: 'チャットで気軽に相談', desc: '見積もり依頼から疑問点まで、チャットで気軽にやり取りできます',       iconBg: 'linear-gradient(135deg,#fef3c7,#fde68a)' },
    { Icon: CheckCircleIcon, title: '工事完了まで安心管理', desc: '進捗確認・書類管理・決済まで、ひとつの画面で完結します',             iconBg: 'linear-gradient(135deg,#d1fae5,#a7f3d0)' },
];

const escrowSteps = [
    { Icon: CreditCardIcon,  title: '顧客が請求書を\n確認して支払い', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)' },
    { Icon: FileCheckIcon,   title: 'のりいれ三丁目が\n施工業者に代わって受領',       bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)' },
    { Icon: CheckSquareIcon, title: '工事完了を\n顧客が確認',         bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)' },
    { Icon: SendIcon,        title: '施工業者へ\n代金を振り込み',         bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)' },
];

const steps = [
    { n: '1', title: '無料登録',        desc: 'アカウント作成',     color: '#3b82f6' },
    { n: '2', title: '業者を検索',      desc: '条件で絞り込み',    color: '#f59e0b' },
    { n: '3', title: 'チャットで見積依頼', desc: '気軽に相談',     color: '#10b981' },
    { n: '4', title: '工事・進捗確認',  desc: 'リアルタイム管理',  color: '#8b5cf6' },
    { n: '5', title: '完了・評価',      desc: 'レビュー投稿',      color: '#ec4899' },
];

const faqs = [
    { q: '利用料金はかかりますか？',               a: '顧客は完全無料です。施工業者は成約時に手数料が発生します。' },
    { q: 'どのような工事に対応していますか？',     a: '乗り入れ工事（車庫前のコンクリート舗装等）を専門としています。' },
    { q: '登録施工業者の信頼性は？',                   a: '全施工業者は事前審査・資格確認済みです。ユーザー評価も公開しています。' },
    { q: '支払いはいつ発生しますか？',             a: '請求書確認後、工事前にお支払いいただきます。工事完了確認後に施工業者へ送金されます。' },
    { q: '工事後に問題があった場合は？',           a: '工事完了確認前であれば、チャットで施工業者に連絡して対応を依頼できます。' },
    { q: '複数の業者に同時に見積もり依頼できますか？', a: 'はい、最大3社まで同時に見積もり依頼が可能です。' },
];

// ── フェードインセクション ──────────────────────────────────────────
const FadeSection = ({ children, delay = 0, style = {} }) => {
    const [ref, inView] = useInView();
    return (
        <div ref={ref} style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(28px)',
            transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
            ...style,
        }}>
            {children}
        </div>
    );
};

// ── YouTube IFrame API 遅延ロード ────────────────────────────────────
const STORY_VIDEO_ID = 'EUNGP3Y5h2Y';
let ytApiPromise = null;
const loadYouTubeApi = () => {
    if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
    if (ytApiPromise) return ytApiPromise;
    ytApiPromise = new Promise((resolve) => {
        const prevReady = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (prevReady) prevReady();
            resolve(window.YT);
        };
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
    });
    return ytApiPromise;
};

// ── 会社紹介動画セクション ────────────────────────────────────────────
const StoryVideoSection = () => {
    const [sectionRef, inView] = useInView(0.3);
    const playerElRef = useRef(null);
    const playerRef = useRef(null);
    const startedRef = useRef(false);
    const [muted, setMuted] = useState(true);
    const [playerReady, setPlayerReady] = useState(false);

    useEffect(() => {
        if (!inView || startedRef.current) return;
        startedRef.current = true;
        loadYouTubeApi().then((YT) => {
            playerRef.current = new YT.Player(playerElRef.current, {
                videoId: STORY_VIDEO_ID,
                playerVars: { autoplay: 1, mute: 1, playsinline: 1, rel: 0, modestbranding: 1, controls: 0, disablekb: 1, iv_load_policy: 3 },
                events: {
                    onReady: (e) => {
                        e.target.mute();
                        e.target.playVideo();
                        setPlayerReady(true);
                    },
                },
            });
        });
    }, [inView]);

    const toggleMute = () => {
        const player = playerRef.current;
        if (!player) return;
        if (muted) {
            player.unMute();
            setMuted(false);
        } else {
            player.mute();
            setMuted(true);
        }
    };

    return (
        <section ref={sectionRef} style={{ background: '#fff', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
            <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px' }}>
                <FadeSection style={{ textAlign: 'center', marginBottom: 40 }}>
                    <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#1a1a2e', marginBottom: 14 }}>
                        「のりいれ」が出来るまで
                    </h2>
                </FadeSection>
                <FadeSection delay={0.1}>
                    <div className="story-video-wrap" style={{ borderRadius: 16, boxShadow: '0 20px 48px rgba(0,0,0,0.14)', border: '2px solid rgba(0,92,83,0.08)', background: '#000' }}>
                        <div ref={playerElRef} />
                        {playerReady && (
                            <button
                                onClick={toggleMute}
                                aria-label={muted ? '音声をオンにする' : '音声をオフにする'}
                                className="story-video-mute-btn"
                            >
                                {muted ? <VolumeXIcon /> : <VolumeIcon />}
                            </button>
                        )}
                    </div>
                </FadeSection>
            </div>
        </section>
    );
};

// ── メイン ─────────────────────────────────────────────────────────
const CustomerTopPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div style={{ background: '#f5f6f8' }}>
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes underlineExpand {
                    from { transform: scaleX(0); }
                    to   { transform: scaleX(1); }
                }
                @keyframes pulseDot {
                    0%,100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.4); opacity: 0.6; }
                }
                @keyframes orbBreath1 {
                    0%,100% { transform: scale(1);   opacity: 0.12; }
                    50%     { transform: scale(1.25); opacity: 0.22; }
                }
                @keyframes orbBreath2 {
                    0%,100% { transform: scale(1);   opacity: 0.06; }
                    50%     { transform: scale(1.35); opacity: 0.16; }
                }
                .hero-text-1 { animation: fadeInUp 0.6s ease 0.1s both; }
                .hero-text-2 { animation: fadeInUp 0.6s ease 0.25s both; }
                .hero-text-3 { animation: fadeInUp 0.6s ease 0.4s both; }
                .hero-text-4 { animation: fadeInUp 0.6s ease 0.55s both; }
                .hero-img    { animation: fadeInUp 0.8s ease 0.35s both; }
                .underline-anim { transform-origin: left; animation: underlineExpand 0.8s ease 0.55s both; }
                .feature-card {
                    background: #fff;
                    border: 1px solid #e0e4ea;
                    border-radius: 12px;
                    padding: 32px 28px;
                    text-align: center;
                    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
                    cursor: default;
                    position: relative;
                    overflow: hidden;
                }
                .feature-card:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 20px 40px rgba(0,92,83,0.12);
                    border-color: transparent;
                }
                .escrow-card {
                    border-radius: 12px;
                    padding: 24px 20px;
                    text-align: center;
                    border: 2px solid #fff;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                }
                .escrow-card:hover { transform: scale(1.06); box-shadow: 0 12px 32px rgba(0,0,0,0.12); }
                .step-circle {
                    border-radius: 50%;
                    color: #fff;
                    font-weight: 700;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 16px;
                    transition: transform 0.5s ease;
                    width: 60px; height: 60px; font-size: 1.3rem;
                }
                .step-wrap:hover .step-circle { transform: scale(1.2) rotate(360deg); }
                .step-wrap { transition: transform 0.25s ease; text-align: center; }
                .step-wrap:hover { transform: translateY(-10px); }
                .faq-item { border: 2px solid #e0e4ea; border-radius: 12px; overflow: hidden; background: #fff; transition: border-color 0.2s, box-shadow 0.2s; }
                .faq-item:hover { border-color: rgba(0,92,83,0.3); box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
                .faq-btn { width: 100%; display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; background: none; border: none; cursor: pointer; text-align: left; transition: background 0.2s; }
                .faq-btn:hover { background: linear-gradient(90deg,rgba(230,242,241,0.4),transparent); }
                .faq-chevron { flex-shrink: 0; background: #e6f2f1; border-radius: 50%; padding: 6px; display: flex; align-items: center; justify-content: center; transition: background 0.2s; color: #005c53; }
                .faq-btn:hover .faq-chevron { background: #005c53; color: #fff; }
                .faq-answer { overflow: hidden; transition: max-height 0.35s ease, opacity 0.35s ease; }
                .cta-btn-primary {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: linear-gradient(135deg,#00a99d,#005c53); color: #fff; border: none; cursor: pointer;
                    box-shadow: 0 8px 24px rgba(0,169,157,0.25); transition: transform 0.2s, box-shadow 0.2s;
                    text-decoration: none;
                }
                .cta-btn-primary:hover { transform: scale(1.05); box-shadow: 0 12px 32px rgba(0,169,157,0.35); color: #fff; }
                .cta-btn-outline {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 14px 28px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: #fff; color: #005c53; border: 2px solid #005c53; cursor: pointer;
                    transition: background 0.2s, color 0.2s, transform 0.2s;
                    text-decoration: none;
                }
                .cta-btn-outline:hover { background: #005c53; color: #fff; transform: scale(1.05); }
                .contractor-cta-btn {
                    display: inline-flex; align-items: center; gap: 8px;
                    padding: 16px 32px; border-radius: 8px; font-weight: 700; font-size: 1rem;
                    background: linear-gradient(135deg,#fff,#f0f0f0); color: #005c53; border: none; cursor: pointer;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.15); transition: transform 0.2s, box-shadow 0.2s;
                    text-decoration: none; white-space: nowrap;
                }
                .contractor-cta-btn:hover { transform: scale(1.05); box-shadow: 0 8px 24px rgba(0,0,0,0.2); color: #005c53; }
                .story-video-wrap { position: relative; width: 100%; padding-bottom: 56.25%; height: 0; overflow: hidden; }
                .story-video-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
                .story-video-mute-btn {
                    position: absolute; bottom: 16px; right: 16px; width: 44px; height: 44px; border-radius: 50%;
                    background: rgba(0,0,0,0.6); border: none; cursor: pointer; display: flex; align-items: center;
                    justify-content: center; box-shadow: 0 2px 10px rgba(0,0,0,0.35); transition: background 0.2s; z-index: 2;
                    transform: translateZ(0); will-change: transform; isolation: isolate;
                }
                .story-video-mute-btn:hover { background: rgba(0,0,0,0.75); }
                .float-card-1 { position: absolute; bottom: -16px; left: -16px; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); border-radius: 12px; padding: 14px 18px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); border: 2px solid rgba(0,92,83,0.1); }
                .float-card-2 { position: absolute; top: -16px; right: -16px; background: linear-gradient(135deg,#fbbf24,#f59e0b); border-radius: 12px; padding: 14px 18px; box-shadow: 0 8px 32px rgba(0,0,0,0.12); }
                @media (max-width: 1200px) and (min-width: 769px) {
                    .features-grid { grid-template-columns: 1fr 1fr !important; }
                }
                @media (max-width: 768px) {
                    .hero-grid { grid-template-columns: 1fr !important; }
                    .hero-img-col { display: none !important; }
                    .features-grid { grid-template-columns: 1fr !important; }
                    .escrow-grid { grid-template-columns: 1fr 1fr !important; }
                    .steps-grid { grid-template-columns: 1fr 1fr !important; }
                    .contractor-inner { flex-direction: column !important; text-align: center !important; }
                }
            `}</style>

            {/* ── Hero ─────────────────────────────────────────────── */}
            <section style={{
                background: 'linear-gradient(135deg,#f0fffe 0%,#ffffff 50%,rgba(254,243,199,0.3) 100%)',
                overflow: 'hidden', position: 'relative',
            }}>
                {/* Grid pattern */}
                <div style={{
                    position: 'absolute', inset: 0, opacity: 0.04,
                    backgroundImage: 'linear-gradient(#005c53 1px,transparent 1px),linear-gradient(90deg,#005c53 1px,transparent 1px)',
                    backgroundSize: '50px 50px',
                }} />
                {/* Orbs */}
                <div style={{ position: 'absolute', top: 0, right: '20%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.1),transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: 0, left: '20%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle,rgba(251,191,36,0.08),transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />

                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '80px 24px 96px', position: 'relative' }}>
                    <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
                        {/* 左: テキスト */}
                        <div>
                            <div className="hero-text-1" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 16px', background: 'linear-gradient(90deg,#fbbf24,#f59e0b,#fbbf24)', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, color: '#141c3d', marginBottom: 24 }}>
                                <span style={{ width: 8, height: 8, background: '#141c3d', borderRadius: '50%', animation: 'pulseDot 1.8s ease infinite' }} />
                                代替決済で安心
                            </div>

                            <h1 className="hero-text-2" style={{ fontSize: 'clamp(2.2rem,5vw,3.8rem)', fontWeight: 900, color: '#141c3d', lineHeight: 1.2, marginBottom: 20 }}>
                                乗り入れ工事は、<br />
                                <span style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
                                    <span style={{ background: 'linear-gradient(90deg,#00a99d,#005c53,#004540)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', position: 'relative', zIndex: 1 }}>
                                        地元の専門業者へ
                                    </span>
                                    <span className="underline-anim" style={{ position: 'absolute', bottom: 4, left: 0, right: 0, height: 12, background: 'linear-gradient(90deg,rgba(0,169,157,0.2),rgba(251,191,36,0.2))', zIndex: 0 }} />
                                </span>
                            </h1>

                            <p className="hero-text-3" style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: 1.8, marginBottom: 32, maxWidth: 480 }}>
                                見積もりから完了まで、安心の決済システムで<br />最後まで安全にお任せください
                            </p>

                            <div className="hero-text-4" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                                <Link to="/customer/login?tab=register" className="cta-btn-primary">
                                    無料で業者を探す <ArrowRightIcon color="#fff" />
                                </Link>
                                <Link to="/contractor/register" className="cta-btn-outline">
                                    施工業者として登録する
                                </Link>
                            </div>
                        </div>

                        {/* 右: 画像グリッド */}
                        <div className="hero-img-col hero-img" style={{ position: 'relative' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                <div style={{ gridColumn: '1 / -1', borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 48px rgba(0,0,0,0.14)', border: '2px solid rgba(0,92,83,0.08)', position: 'relative' }}>
                                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,rgba(0,169,157,0.08),transparent)', zIndex: 1 }} />
                                    <img src="/images/top-01.jpeg" alt="乗り入れ工事" style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                                </div>
                                <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(251,191,36,0.2)', position: 'relative' }}>
                                    <img src="/images/top-02.jpeg" alt="工事作業" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
                                </div>
                                <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', border: '2px solid rgba(16,185,129,0.2)', position: 'relative' }}>
                                    <img src="/images/top-03.jpeg" alt="完成した工事" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
                                </div>
                            </div>
                            <div className="float-card-1">
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#005c53' }}>安心決済</div>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', fontWeight: 500 }}>安心決済対応</div>
                            </div>
                            <div className="float-card-2">
                                <div style={{ fontSize: '1rem', fontWeight: 800, color: '#fff' }}>チャットで簡単相談</div>
                                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>最短当日に施工業者から連絡</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 特徴3点 ───────────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(180deg,#fff 0%,#f0fffe 50%,#fff 100%)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.05),transparent)', filter: 'blur(40px)' }} />
                <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, marginBottom: 12 }}>
                            <span style={{ background: 'linear-gradient(90deg,#005c53,#00a99d,#005c53)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                のりいれ三丁目が選ばれる理由
                            </span>
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1rem' }}>安心・安全な工事マッチングを実現する4つの特徴</p>
                    </FadeSection>

                    <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }}>
                        {features.map(({ Icon, title, desc, iconBg }, i) => (
                            <FadeSection key={i} delay={i * 0.1}>
                                <div className="feature-card">
                                    <div style={{ display: 'inline-flex', padding: 16, background: iconBg, borderRadius: 16, marginBottom: 24 }}>
                                        <Icon />
                                    </div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{title}</h3>
                                    <p style={{ fontSize: '0.9rem', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{desc}</p>
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 安心決済 ────────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(135deg,#f5f6f8,rgba(254,243,199,0.15),#f5f6f8)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle at 20px 20px,#005c53 1px,transparent 0)', backgroundSize: '40px 40px' }} />
                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <div style={{ display: 'inline-block', padding: '6px 18px', background: 'linear-gradient(90deg,#fbbf24,#f59e0b)', color: '#fff', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>
                            安全な決済システム
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#1a1a2e', marginBottom: 14 }}>
                            先払いでも安心の<br />決済システム
                        </h2>
                        <p style={{ fontSize: '1rem', color: '#6b7280', maxWidth: 560, margin: '0 auto' }}>
                            工事完了が確認されるまで、施工業者への代金のお支払いは行われません
                        </p>
                    </FadeSection>

                    <div className="escrow-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20 }}>
                        {escrowSteps.map(({ Icon, title, bg }, i) => (
                            <FadeSection key={i} delay={i * 0.15} style={{ position: 'relative' }}>
                                <div className="escrow-card" style={{ background: bg }}>
                                    <div style={{ display: 'inline-flex', padding: 14, background: 'rgba(255,255,255,0.7)', borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                                        <Icon />
                                    </div>
                                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#005c53', marginBottom: 8, letterSpacing: '0.05em' }}>STEP {i + 1}</div>
                                    <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a2e', whiteSpace: 'pre-line', margin: 0, lineHeight: 1.6 }}>{title}</p>
                                </div>
                                {i < escrowSteps.length - 1 && (
                                    <div style={{ display: 'none', position: 'absolute', top: '50%', right: -14, transform: 'translateY(-50%)', background: '#fff', borderRadius: '50%', padding: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 10 }} className="escrow-arrow">
                                        <ArrowRightIcon size={16} />
                                    </div>
                                )}
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── ご利用の流れ ──────────────────────────────────────── */}
            <section style={{ background: '#fff', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,transparent,rgba(230,242,241,0.25),transparent)' }} />
                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 56 }}>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, marginBottom: 12 }}>
                            <span style={{ background: 'linear-gradient(90deg,#005c53,#00a99d)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                                ご利用の流れ
                            </span>
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1rem' }}>たった5ステップで理想の業者に出会えます</p>
                    </FadeSection>

                    <div className="steps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 20 }}>
                        {steps.map((step, i) => (
                            <FadeSection key={i} delay={i * 0.1}>
                                <div className="step-wrap" style={{ position: 'relative' }}>
                                    <div className="step-circle" style={{ background: `linear-gradient(135deg,${step.color},${step.color}bb)`, boxShadow: `0 4px 16px ${step.color}44` }}>
                                        {step.n}
                                    </div>
                                    <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{step.title}</h3>
                                    <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>{step.desc}</p>
                                    {i < steps.length - 1 && (
                                        <div style={{ position: 'absolute', top: 28, right: -12 }}>
                                            <ArrowRightIcon color="rgba(107,114,128,0.35)" size={18} />
                                        </div>
                                    )}
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 会社紹介動画 ──────────────────────────────────────── */}
            <StoryVideoSection />

            {/* ── FAQ ──────────────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(180deg,#f5f6f8,#fff)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,169,157,0.08),transparent)', filter: 'blur(48px)' }} />
                <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection style={{ textAlign: 'center', marginBottom: 48 }}>
                        <div style={{ display: 'inline-block', padding: '6px 18px', background: 'linear-gradient(90deg,#005c53,#00a99d)', color: '#fff', borderRadius: 20, fontSize: '0.82rem', fontWeight: 700, marginBottom: 16 }}>
                            FAQ
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>よくある質問</h2>
                    </FadeSection>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {faqs.map((faq, i) => (
                            <FadeSection key={i} delay={i * 0.05}>
                                <div className="faq-item">
                                    <button className="faq-btn" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                        <span style={{ fontWeight: 500, color: '#1a1a2e', fontSize: '0.95rem', paddingRight: 16, transition: 'color 0.2s' }}>
                                            {faq.q}
                                        </span>
                                        <span className="faq-chevron">
                                            <ChevronDownIcon open={openFaq === i} />
                                        </span>
                                    </button>
                                    <div className="faq-answer" style={{ maxHeight: openFaq === i ? 200 : 0, opacity: openFaq === i ? 1 : 0 }}>
                                        <div style={{ padding: '0 24px 20px' }}>
                                            <div style={{ background: 'linear-gradient(90deg,rgba(230,242,241,0.5),transparent)', borderLeft: '4px solid #005c53', borderRadius: '0 8px 8px 0', padding: '12px 16px', color: '#1a1a2e', fontSize: '0.9rem', lineHeight: 1.7 }}>
                                                {faq.a}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </FadeSection>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── 業者募集CTA ───────────────────────────────────────── */}
            <section style={{ background: 'linear-gradient(135deg,#141c3d,#1a2847,#141c3d)', padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 360, height: 360, borderRadius: '50%', background: '#fbbf24', filter: 'blur(80px)', animation: 'orbBreath1 8s ease-in-out infinite' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, width: 360, height: 360, borderRadius: '50%', background: '#00a99d', filter: 'blur(80px)', animation: 'orbBreath2 10s ease-in-out infinite' }} />

                <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
                    <FadeSection>
                        <div className="contractor-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: '48px 52px', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'inline-block', padding: '5px 16px', background: 'linear-gradient(90deg,#fbbf24,#f59e0b)', color: '#141c3d', borderRadius: 20, fontSize: '0.8rem', fontWeight: 700, marginBottom: 16 }}>
                                    施工業者募集中
                                </div>
                                <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,1.9rem)', fontWeight: 700, color: '#fff', marginBottom: 12 }}>
                                    工事業者の方はこちら
                                </h2>
                                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', margin: 0 }}>
                                    新規案件の獲得・書類管理・入金まで一括サポート
                                </p>
                            </div>
                            <Link to="/contractor/register" className="contractor-cta-btn">
                                施工業者として登録する <ArrowRightIcon color="#005c53" />
                            </Link>
                        </div>
                    </FadeSection>
                </div>
            </section>

        </div>
    );
};

export default CustomerTopPage;
